import PageHeaderUtils from 'utils/page-header';
import { ApolloClient } from '@apollo/client';
import {
  CommonQueryFiltersType,
  ItemType,
  MENU_SECTIONS,
  ProductSelection,
  ProductsQueryOptionsArgs,
  ProductType,
} from 'components/ProductsSelection/types';
import { FilterSelectionType, ProductDataType, StoreDataType } from 'utils/types';
import { HeliosProduct, Store } from 'generated/products-graphql';
import { getArrayOfKeys } from 'utils/functions';

export const dataMappers = {
  accounts: {
    dataKeyMapper: (item) => {
      return item.name;
    },
    dataItemMapper: (item) => {
      return { id: item.name, label: item.name };
    },
  },
  states: {
    dataKeyMapper: (item) => item.code,
    dataItemMapper: (item) => ({ id: item.code, label: item.code }),
  },
  stores: {
    dataKeyMapper: (item) => PageHeaderUtils.createStoreId(item.spinsTag, item.nielsenTag),
    dataItemMapper: (item) => ({
      id: PageHeaderUtils.createStoreId(item.spinsTag, item.nielsenTag),
      label: `${item.name} - ${item.address}, ${item.city} ${item.state} ${item.postalCode}`,
    }),
  },
  items: {
    dataKeyMapper: (item) => item.upc,
    dataItemMapper: (item) => ({ id: item.upc, label: item.name }),
  },
};

export const createLabelsForFilters = (
  productType: ProductType,
  data: Array<Store | HeliosProduct>,
  appliedSelection: ProductSelection
): ProductSelection => {
  // Remove element from data
  const dataFiltered: Array<Store | HeliosProduct> = data
    .map((itemData) => {
      const obj = appliedSelection[productType].find((appliedFilter) => {
        if (productType === 'stores') {
          return (
            appliedFilter.id ===
            PageHeaderUtils.createStoreId(
              (itemData as StoreDataType).spinsTag,
              (itemData as StoreDataType).nielsenTag
            )
          );
        } else {
          return appliedFilter.id === (itemData as ProductDataType).upc;
        }
      });
      return obj ? itemData : null;
    })
    .filter((elem) => elem);

  // Add data to appliedFilters
  dataFiltered.forEach((itemData) => {
    let item;

    if (productType === 'stores') {
      item = itemData as StoreDataType;
    } else {
      item = itemData as ProductDataType;
    }

    const newArray = appliedSelection[productType].filter((appliedFilter) => {
      if (productType === 'stores') {
        return appliedFilter.id !== PageHeaderUtils.createStoreId(item.spinsTag, item.nielsenTag);
      } else {
        return appliedFilter.id !== item.upc;
      }
    });
    // REMINDER: if item.upc and item.name are equal, item should not be added to the mapped selection.
    const obj: ItemType =
      productType === 'stores'
        ? {
            id: PageHeaderUtils.createStoreId(item.spinsTag, item.nielsenTag),
            label: `${item.name} - ${item.address}, ${item.city} ${item.state} ${item.postalCode}`,
          }
        : {
            id: item.upc,
            label: item.name,
          };
    appliedSelection = {
      ...appliedSelection,
      [productType]: newArray.concat([obj]),
    };
  });
  return appliedSelection;
};

export const getMenuFilters = (
  menuSections: Array<ProductType>,
  selection: any,
  currentProductType: ProductType
) => {
  let menuQueryFilters: FilterSelectionType = {
    accounts: [],
    states: [],
    stores: [],
    items: [],
  };
  menuSections.forEach((section: ProductType) => {
    if (currentProductType !== section) {
      menuQueryFilters = {
        ...menuQueryFilters,
        [section]: getArrayOfKeys(selection[section], 'id'),
      };
    }
  });
  return menuQueryFilters;
};

export const getQueryOptions = (
  options: ProductsQueryOptionsArgs,
  commonQueryFilters: CommonQueryFiltersType,
  selectionApplied: any,
  client: ApolloClient<any>
) => {
  return {
    defaultSortAttribute: options.defaultSortAttribute,
    defaultSearchAttribute: options.defaultSearchAttribute,
    queryVariables: {
      ...commonQueryFilters,
      ...getMenuFilters(MENU_SECTIONS, selectionApplied, options.productType),
      products: options.productType === 'items' ? [] : getArrayOfKeys(selectionApplied.items, 'id'),
    },
    resultAttribute: options.resultAttribute,
    client,
  };
};
